import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ILocale } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { actionProcessing } from "infrastructure/store";
import { getScreenExtraReducers } from "ui/app";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";
import { ITenant, IEnterprise, IIndustry } from "ui/shared/models/common";

import { createTextPreview } from "../shared/helpers/TextPreview.helpers";
import { IChangeToken } from "../shared/types/Token.types";

import {
  ITranslationMessagesManagerState,
  ITranslationMessagesManagerFilter,
  ITranslationMessageItem,
  getInitialTranslationMessagesManagerState,
  ITranslationMessagesManagerFilterData,
  ITranslationMessageRow,
  IChangeText,
  ITranslationMessageModifyData
} from "./TranslationMessages.state";

const initialState = getInitialTranslationMessagesManagerState();

export const translationMessages = createSlice({
  name: "translationMessagesManager",
  initialState: getInitialTranslationMessagesManagerState(),
  reducers: {
    loadingFailed: (state, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, notifications: [...state.notifications, action.payload] };
    },

    initialize: state => {
      return { ...state, isLoading: true };
    },

    initializeCompleted: (state, action: PayloadAction<ITranslationMessagesManagerFilter>) => {
      return { ...state, isLoading: false, filter: action.payload };
    },

    search: actionProcessing.createWithClearNotification((state: ITranslationMessagesManagerState, action: PayloadAction<ITranslationMessagesManagerFilterData>) => {
      return {
        ...state,
        searchQuery: action.payload,
        searchResult: undefined,
        isLoading: true
      };
    }),

    searchCompleted: (state, action: PayloadAction<ITranslationMessageRow[]>) => {
      return {
        ...state,
        isLoading: false,
        grid: { gridData: action.payload, selected: [], isSearched: true },
        isExportDisabled: action.payload.length === 0
      };
    },

    searchTranslationMessage: actionProcessing.createWithClearNotification(
      (state: ITranslationMessagesManagerState, action: PayloadAction<ITranslationMessagesManagerFilterData>) => {
        return {
          ...state,
          searchQuery: action.payload,
          searchResult: undefined,
          isLoading: true
        };
      }
    ),

    searchTranslationMessageCompleted: actionProcessing.createWithClearNotification((state: ITranslationMessagesManagerState, action: PayloadAction<ITranslationMessageItem>) => {
      return {
        ...state,
        searchResult: action.payload,
        selectedRowId: "",
        isLoading: false
      };
    }),

    searchTranslationMessageFailed: (state: ITranslationMessagesManagerState, action: PayloadAction<INotification>) => {
      return {
        ...state,
        searchResult: undefined,
        notifications: [...state.notifications, action.payload],
        isLoading: false
      };
    },

    loadIndustries: (state, _: PayloadAction<IPickerSearchCriterion<IIndustry>>) => {
      return { ...state, isLoading: true, filter: { ...state.filter, industryCanOpen: undefined } };
    },

    loadIndustriesCompleted: (state, action: PayloadAction<IIndustry[]>) => {
      return { ...state, isLoading: false, filter: { ...state.filter, industries: action.payload } };
    },

    loadEnterprises: (state, _: PayloadAction<IPickerSearchCriterion<IEnterprise>>) => {
      return { ...state, isLoading: true, filter: { ...state.filter, enterpriseCanOpen: undefined } };
    },

    loadEnterprisesCompleted: (state, action: PayloadAction<IEnterprise[]>) => {
      return { ...state, isLoading: false, filter: { ...state.filter, enterprises: action.payload } };
    },

    loadTenants: (state, _: PayloadAction<IPickerSearchCriterion<ITenant>>) => {
      return { ...state, isLoading: true, filter: { ...state.filter, tenantCanOpen: undefined } };
    },

    loadTenantsCompleted: (state, action: PayloadAction<ITenant[]>) => {
      return { ...state, isLoading: false, filter: { ...state.filter, tenants: action.payload } };
    },

    loadLocales: (state, _: PayloadAction<IPickerSearchCriterion<ILocale>>) => {
      return { ...state, isLoading: true, filter: { ...state.filter, localeCanOpen: undefined } };
    },

    loadLocalesCompleted: (state, action: PayloadAction<ILocale[]>) => {
      return { ...state, isLoading: false, filter: { ...state.filter, locales: action.payload } };
    },

    loadSupportedLocales: state => {
      return {
        ...state,
        filter: { ...state.filter, supportedLocales: undefined }
      };
    },

    loadSupportedLocalesCompleted: (state, action: PayloadAction<ILocale[]>) => {
      return { ...state, filter: { ...state.filter, supportedLocales: action.payload } };
    },

    loadSupportedLocalesFailed: (state, action: PayloadAction<INotification>) => {
      return { ...state, filter: { ...state.filter, supportedLocales: [] }, notifications: [...state.notifications, action.payload] };
    },

    selectTranslations: (state: ITranslationMessagesManagerState, action: PayloadAction<ITranslationMessageRow[]>) => {
      return {
        ...state,
        grid: { ...state.grid, selected: action.payload }
      };
    },

    changeTranslation: (state, _action: PayloadAction<IChangeText>) => {
      return state;
    },

    changeTokenValue: (state, _action: PayloadAction<IChangeToken>) => {
      return {
        ...state
      };
    },

    applyChangeTokenValue: (state, { payload }: PayloadAction<IChangeToken>) => {
      const gridData = [...state.grid.gridData];
      const row = gridData[payload.rowIndex];
      const tokens = [...row.data.tokens];
      const token = row.data.tokens[payload.tokenIndex];

      tokens[payload.tokenIndex] = {
        index: token.index,
        name: token.name,
        description: token.description,
        type: token.type,
        value: payload.value,
        displayValue: payload.displayValue
      };

      const translatedText = row.data.translatedText ? row.data.translatedText : "";
      const textPreview = createTextPreview(translatedText, tokens);

      gridData[payload.rowIndex] = {
        ...row,
        data: {
          ...row.data,
          tokens: tokens,
          textPreview: textPreview
        }
      };

      const selected: ITranslationMessageRow[] = state.grid.selected.length ? gridData.filter(d => state.grid.selected.find(s => s.index === d.index)) : [];

      return { ...state, grid: { ...state.grid, gridData: gridData, selected: [...selected] } };
    },

    applyTranslationChange: (state, { payload }: PayloadAction<ITranslationMessageModifyData>) => {
      const gridData = [...state.grid.gridData];
      const row = gridData[payload.index];

      const translatedText = payload.translatedText ? payload.translatedText : "";
      const textPreview = createTextPreview(translatedText, row.data.tokens);

      gridData[payload.index] = {
        ...row,
        originalData: payload.originalData,
        isTranslationChanged: payload.isTranslationChanged,
        validationIssues: payload.validationIssues,
        data: {
          ...row.data,
          translatedText: payload.translatedText,
          messageId: payload.messageId,
          textPreview: textPreview
        }
      };

      const selected: ITranslationMessageRow[] = state.grid.selected.length ? gridData.filter(d => state.grid.selected.find(s => s.index === d.index)) : [];

      return { ...state, grid: { ...state.grid, gridData: gridData, selected: [...selected] } };
    },

    applyTranslationsChanges: (state, { payload }: PayloadAction<ITranslationMessageModifyData[]>) => {
      const gridData = [...state.grid.gridData];

      payload.forEach(data => {
        const row = gridData[data.index];

        gridData[data.index] = {
          ...row,
          originalData: data.originalData,
          isTranslationChanged: data.isTranslationChanged,
          validationIssues: data.validationIssues,
          data: {
            ...row.data,
            translatedText: data.translatedText,
            messageId: data.messageId
          }
        };
      });

      const selected: ITranslationMessageRow[] = state.grid.selected.length ? gridData.filter(d => state.grid.selected.find(s => s.index === d.index)) : [];

      return { ...state, grid: { ...state.grid, gridData: gridData, selected: [...selected] } };
    },

    applyTranslationChangesWarning: (state, action: PayloadAction<INotification | undefined>) => {
      if (action.payload) {
        return { ...state, notifications: [action.payload] };
      } else {
        return { ...state, notifications: [] };
      }
    },

    saveTranslations: actionProcessing.createWithClearNotification((state: ITranslationMessagesManagerState) => {
      return { ...state, isLoading: true };
    }),

    saveTranslationsCompleted: (state, { payload }: PayloadAction<{ message: INotification; isLoading?: boolean }>) => {
      if (payload.isLoading !== undefined) {
        state.isLoading = payload.isLoading;
      }
      state.notifications.push(payload.message);
      return state;
    },

    applyFilter: (state: ITranslationMessagesManagerState, action: PayloadAction<ITranslationMessagesManagerFilterData>) => {
      return {
        ...state,
        isLoading: false,
        filter: { ...state.filter, lastData: action.payload }
      };
    },

    exportTranslationsToXml: (state: ITranslationMessagesManagerState) => {
      return state;
    },

    downloadTranslationUploadTemplate: (state: ITranslationMessagesManagerState) => {
      return {
        ...state
      };
    },
    onTranslationUploadCompleted: (state, action: PayloadAction<INotification>) => {
      return {
        ...state,
        notifications: [action.payload]
      };
    },

    obsoleteTranslations: actionProcessing.createWithClearNotification((state: ITranslationMessagesManagerState) => {
      return { ...state, isLoading: true };
    }),

    obsoleteTranslationsCompleted: (state, action: PayloadAction<INotification>) => {
      return { ...state, notifications: [action.payload], grid: { ...state.grid, selected: [] } };
    }
  },

  extraReducers: getScreenExtraReducers<ITranslationMessagesManagerState>("notifications", initialState)
});
